import React, { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import MediaUploader from "./MediaUploader";
import { DEFAULT_TIERS, DEFAULT_VARIANTS } from "@/lib/pricing";
import { BADGE_ICON_OPTIONS, DynIcon } from "@/lib/icons";

const STOCK_OPTIONS = ["In Stock", "Low", "Sold Out"];

const empty = {
  name: "", code: "", category: "", price: 0, sale_price: null, delivery_fee: null, hot_steal: false,
  tiers: DEFAULT_TIERS.map((t) => ({ ...t })), variants: [], badges: [], profile_nose: "", summary: "",
  stock: "In Stock", visible: true, media: [],
};

const inputCls = "w-full bg-[#141414] border border-[#333] rounded-lg h-11 px-3 text-[#F0EDEA] focus:border-[#E60000] outline-none";
const labelCls = "block text-[#A8A2A0] text-xs uppercase tracking-wide mb-1 mt-4";

export default function ProductForm({ product, categories, onClose, onSaved, onCategoryAdded }) {
  const [form, setForm] = useState(empty);
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({ ...empty, ...product, tiers: product.tiers?.length ? product.tiers : empty.tiers, variants: product.variants || [], badges: product.badges || [], media: product.media || [] });
    } else setForm(empty);
  }, [product]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    let category = form.category;
    if (newCat.trim()) {
      try {
        const { data } = await api.post("/admin/categories", { name: newCat.trim() });
        category = data.name; onCategoryAdded?.();
      } catch { /* maybe exists */ category = newCat.trim(); }
    }
    const payload = {
      ...form, category,
      price: Number(form.price) || 0,
      sale_price: form.sale_price === "" || form.sale_price == null ? null : Number(form.sale_price),
      delivery_fee: form.delivery_fee === "" || form.delivery_fee == null ? null : Number(form.delivery_fee),
      tiers: form.tiers.map((t) => ({ units: Number(t.units), discount: Number(t.discount) })),
      variants: (form.variants || []).map((v) => ({
        label: v.label,
        price: Number(v.price) || 0,
        delivery_fee: v.delivery_fee === "" || v.delivery_fee == null ? null : Number(v.delivery_fee),
        enabled: v.enabled !== false,
      })),
    };
    try {
      if (product?.id) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const addBadge = () => set("badges", [...form.badges, { icon: "star", label: "" }]);
  const setBadge = (i, k, v) => set("badges", form.badges.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const removeBadge = (i) => set("badges", form.badges.filter((_, idx) => idx !== i));
  const setTier = (i, k, v) => set("tiers", form.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t));

  const setVariant = (i, k, v) => set("variants", form.variants.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addVariant = () => set("variants", [...(form.variants || []), { label: "", price: 0, enabled: true }]);
  const removeVariant = (i) => set("variants", form.variants.filter((_, idx) => idx !== i));
  const addDefaultVariants = () => set("variants", DEFAULT_VARIANTS.map((v) => ({ ...v })));

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-end sm:items-center justify-center" data-testid="product-form-modal">
      <div className="bg-[#111] border border-[#333] w-full sm:max-w-lg sm:rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#111] border-b border-[#333] px-5 h-14 flex items-center justify-between z-10">
          <h2 className="font-heading font-bold uppercase text-[#F0EDEA]">{product ? "Edit" : "Add"} Product</h2>
          <button onClick={onClose} className="text-[#A8A2A0]" data-testid="close-product-form"><X size={24} /></button>
        </div>
        <div className="p-5">
          <label className={labelCls}>Name</label>
          <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" />

          <label className={labelCls}>Item Code</label>
          <input className={inputCls} value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="pf-code" />

          <label className={labelCls}>Category</label>
          <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category">
            <option value="">— Select —</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <input className={`${inputCls} mt-2`} placeholder="...or add new category" value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="pf-new-category" />

          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Price ($)</label><input type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" /></div>
            <div><label className={labelCls}>Sale Price ($)</label><input type="number" className={inputCls} value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value)} data-testid="pf-sale-price" /></div>
          </div>

          <label className={labelCls}>Custom Shipping Price ($, optional)</label>
          <input type="number" className={inputCls} placeholder="Leave blank to use the default delivery fee" value={form.delivery_fee ?? ""} onChange={(e) => set("delivery_fee", e.target.value)} data-testid="pf-delivery-fee" />
          <p className="text-[#A8A2A0] text-xs mt-1">Applies to local delivery for this product. A per-size delivery fee (below) overrides this.</p>

          <div className="flex items-center gap-6 mt-4">
            <Toggle label="Hot Steal" checked={form.hot_steal} onChange={(v) => set("hot_steal", v)} testid="pf-hotsteal" />
            <Toggle label="Visible" checked={form.visible} onChange={(v) => set("visible", v)} testid="pf-visible" />
          </div>

          <label className={labelCls}>Size Options & Pricing</label>
          <p className="text-[#A8A2A0] text-xs mb-2">Add sizes (e.g. 1 unit, 1/2 unit, 1/4 unit, 1 zip) with their own price and local delivery fee. Leave empty to sell at the single Price above.</p>
          <div className="space-y-2">
            {(form.variants || []).map((v, i) => (
              <div key={i} className="bg-[#141414] border border-[#333] rounded-lg p-2 space-y-2" data-testid={`pf-variant-${i}`}>
                <div className="flex items-center gap-2">
                  <input className={`${inputCls} h-10`} placeholder="Size label" value={v.label} onChange={(e) => setVariant(i, "label", e.target.value)} data-testid={`pf-variant-label-${i}`} />
                  <button type="button" onClick={() => setVariant(i, "enabled", v.enabled === false)} className={`text-xs px-2 py-1 rounded border shrink-0 ${v.enabled !== false ? "border-green-600 text-green-400" : "border-[#333] text-[#A8A2A0]"}`} data-testid={`pf-variant-enabled-${i}`}>{v.enabled !== false ? "On" : "Off"}</button>
                  <button type="button" onClick={() => removeVariant(i)} className="text-[#A8A2A0] hover:text-[#E60000] shrink-0"><Trash2 size={16} /></button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#A8A2A0] text-xs w-14">Price $</span>
                  <input type="number" className={`${inputCls} h-9`} placeholder="Price" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} data-testid={`pf-variant-price-${i}`} />
                  <span className="text-[#A8A2A0] text-xs w-20">Delivery $</span>
                  <input type="number" className={`${inputCls} h-9`} placeholder="Delivery" value={v.delivery_fee ?? ""} onChange={(e) => setVariant(i, "delivery_fee", e.target.value)} data-testid={`pf-variant-delivery-${i}`} />
                </div>
              </div>
            ))}
            <div className="flex gap-4">
              <button type="button" onClick={addVariant} className="flex items-center gap-1 text-[#E60000] text-sm font-semibold" data-testid="pf-add-variant"><Plus size={16} /> Add size</button>
              {(!form.variants || form.variants.length === 0) && (
                <button type="button" onClick={addDefaultVariants} className="text-[#A8A2A0] text-sm font-semibold hover:text-[#E60000]" data-testid="pf-add-default-variants">Add default sizes</button>
              )}
            </div>
          </div>

          <label className={labelCls}>Bundle Discount Tiers (per unit)</label>
          <div className="space-y-2">
            {form.tiers.map((t, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="number" className={`${inputCls} h-10`} value={t.units} onChange={(e) => setTier(i, "units", e.target.value)} data-testid={`pf-tier-units-${i}`} />
                <span className="text-[#A8A2A0] text-sm">units →</span>
                <span className="text-[#A8A2A0]">$</span>
                <input type="number" className={`${inputCls} h-10`} value={t.discount} onChange={(e) => setTier(i, "discount", e.target.value)} data-testid={`pf-tier-disc-${i}`} />
                <span className="text-[#A8A2A0] text-sm">off ea</span>
              </div>
            ))}
          </div>

          <label className={labelCls}>Badges</label>
          <div className="space-y-2">
            {form.badges.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <select className={`${inputCls} h-10 w-28`} value={b.icon} onChange={(e) => setBadge(i, "icon", e.target.value)}>
                  {BADGE_ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <DynIcon name={b.icon} size={18} className="text-[#E60000]" />
                <input className={`${inputCls} h-10`} placeholder="Label" value={b.label} onChange={(e) => setBadge(i, "label", e.target.value)} data-testid={`pf-badge-label-${i}`} />
                <button onClick={() => removeBadge(i)} className="text-[#A8A2A0] hover:text-[#E60000]"><Trash2 size={16} /></button>
              </div>
            ))}
            <button onClick={addBadge} className="flex items-center gap-1 text-[#E60000] text-sm font-semibold" data-testid="pf-add-badge"><Plus size={16} /> Add badge</button>
          </div>

          <label className={labelCls}>Profile / Nose</label>
          <input className={inputCls} value={form.profile_nose} onChange={(e) => set("profile_nose", e.target.value)} data-testid="pf-profile" />

          <label className={labelCls}>Summary</label>
          <textarea className={`${inputCls} h-24 py-2`} value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="pf-summary" />

          <label className={labelCls}>Stock</label>
          <select className={inputCls} value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock">
            {STOCK_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <label className={labelCls}>Media (photos & videos)</label>
          <MediaUploader value={form.media} onChange={(m) => set("media", m)} />

          <div className="flex gap-3 mt-6">
            <button onClick={onClose} className="btn-skew btn-secondary flex-1 text-sm"><span>Cancel</span></button>
            <button onClick={save} disabled={busy} className="btn-skew btn-primary flex-1 text-sm" data-testid="pf-save"><span>{busy ? "Saving..." : "Save"}</span></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex items-center gap-2" data-testid={testid}>
      <span className={`w-11 h-6 rounded-full transition-colors relative ${checked ? "bg-[#E60000]" : "bg-[#333]"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : ""}`} />
      </span>
      <span className="text-[#F0EDEA] text-sm">{label}</span>
    </button>
  );
}
